import { ListChecks, Mail } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog'
import type { QuickAction, QuickMenuItem } from '@/components/patterns/QuickActions'
import { RecordLayout } from '@/components/patterns/RecordLayout'
import { focusTab } from '@/components/patterns/recordTabModel'
import type { RecordTab } from '@/components/patterns/RecordTabs'
import { Skeleton } from '@/components/ui/skeleton'
import { plainTextToHtml } from '@/lib/richText'
import { toast } from '@/lib/toast'
import { DESKTOP_QUERY, useMediaQuery } from '@/lib/useMediaQuery'
import type { LabelLike } from '../components/LabelChip'
import { clearCall } from '../record/callPrompt'
import { CallPromptBanner } from '../record/CallPromptBanner'
import { MergeNotice } from '../merges/MergeNotice'
import { useInsprekenEntry } from '../inspreken/useInsprekenEntry'
import { SummaryEditor } from '../components/SummaryEditor'
import { AgreementDialog } from '../agreements/AgreementDialog'
import { AgreementsSection } from '../agreements/AgreementsSection'
import { AgreementsTab } from '../agreements/AgreementsTab'
import { ArchiveRelationDialog } from '../agreements/ArchiveRelationDialog'
import { rhythmState } from '../agreements/format'
import { useOwnerChange } from '../agreements/useOwnerChange'
import { useRelationAgreements } from '../agreements/useRelationAgreements'
import { ContactMailDialog } from '../ContactMailDialog'
import { CrmApiError } from '../http'
import { ACTIVITY_ICONS } from '../record/labels'
import { RecordBackLink } from '../record/RecordBackLink'
import { RelationKeyFacts } from '../record/RelationKeyFacts'
import { ActivityComposer, type ComposerRelation, type ComposerType } from '../timeline/ActivityComposer'
import { parseChip, type ChipKey } from '../timeline/chips'
import { PlannedSection } from '../timeline/PlannedSection'
import { RecentActivity } from '../timeline/RecentActivity'
import { TaskDialog } from '../timeline/TaskDialog'
import { Timeline } from '../timeline/Timeline'
import type { RelationRef } from '../timeline/types'
import type { Agreement } from '../agreements/types'
import type { CrmContactCardScan, CrmContactDetail, CrmContactUpdate } from '../types'
import { deleteContact, getCardScans, getContactRecord, patchContact, putContactLabels } from './api'
import { ContactAssociations } from './ContactAssociations'
import { ContactHeader } from './ContactHeader'
import { ContactProperties } from './ContactProperties'
import { MergeContactsDialog } from './merge/MergeContactsDialog'
import { PickContactDialog } from './merge/PickContactDialog'
import { NoRelation } from './NoRelation'

const TABS: RecordTab[] = [
  { key: 'overzicht', label: 'Overzicht' },
  { key: 'activiteit', label: 'Activiteit' },
]

/** An answer older than what is on the screen: requests can finish out of order. */
function isOlder(answer: CrmContactDetail, shown: CrmContactDetail): boolean {
  if (!answer.updated_at || !shown.updated_at) return false
  return Date.parse(answer.updated_at) < Date.parse(shown.updated_at)
}

/**
 * The contact page (spec-63 E3, design §4.5) in the HubSpot layout on `RecordLayout`: identity,
 * quick actions and properties on the left; Overzicht and the one timeline "Activiteit" in the
 * middle; the association cards on the right; on a phone one column with tabs. `?tab=` and
 * `?type=` live in the URL. `version` reloads the timeline, Gepland and the recent activity;
 * `factsKey` reloads the contact itself (a refresh keeps the page on screen).
 */
export function ContactRecord({ personId }: { personId: string }) {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const inspreken = useInsprekenEntry({ type: 'contact', id: personId }, { inMore: true })
  const desktop = useMediaQuery(DESKTOP_QUERY)
  const idPrefix = useId()
  const [detail, setDetail] = useState<CrmContactDetail | null>(null)
  const [loadError, setLoadError] = useState<'notfound' | 'failed' | null>(null)
  const [scans, setScans] = useState<CrmContactCardScan[]>([])
  const [version, setVersion] = useState(0)
  const [factsKey, setFactsKey] = useState(0)
  const [callEpoch, setCallEpoch] = useState(0)
  const [composer, setComposer] = useState<{ open: boolean; type: ComposerType; focus: boolean }>({
    open: false,
    type: 'note',
    focus: false,
  })
  const [taskOpen, setTaskOpen] = useState(false)
  const [mailOpen, setMailOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [agreementDialog, setAgreementDialog] = useState<{ open: boolean; agreement?: Agreement }>({ open: false })
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [mergePicking, setMergePicking] = useState(false)
  const [mergeWith, setMergeWith] = useState<string | null>(null)
  const [linkRequest, setLinkRequest] = useState(0)
  // The labels as the person left them while their answer is on its way.
  const [chosenLabels, setChosenLabels] = useState<LabelLike[] | null>(null)
  const loaded = useRef(false)
  const askedDelete = useRef(false)
  const labelSaves = useRef(0)
  const labelQueue = useRef<Promise<void>>(Promise.resolve())
  const actionsRef = useRef<HTMLDivElement>(null)
  const tab = params.get('tab') ?? 'overzicht'
  const typeKey = parseChip(params.get('type'))
  // The list's search, page and sort travel with the link in (`state.from`, a `?…` string).
  const fromState = (location.state as { from?: unknown } | null)?.from
  const from = typeof fromState === 'string' && fromState.startsWith('?') ? fromState : ''
  const listPath = `/relaties/contacten${from}`
  const relation: RelationRef = { kind: 'contact', id: personId }
  const movedTo = detail?.moved_to_organisation_id ?? null
  const refreshFacts = () => setFactsKey((key) => key + 1)
  const refreshAll = () => {
    setVersion((value) => value + 1)
    refreshFacts()
  }
  const { data: agreements, failed: agreementsFailed } = useRelationAgreements(relation, version)
  // A quick action's request for the editor is good for one focus: the composer reports when it
  // has honoured it and the request goes down. Left up, every later mount of the composer (the
  // tabs move it to the other panel) would take the focus again, from the tab that has it.
  const lowerFocusRequest = useCallback(
    () => setComposer((current) => (current.focus ? { ...current, focus: false } : current)),
    [],
  )

  useEffect(() => {
    let cancelled = false
    Promise.all([getContactRecord(personId), getCardScans(personId).catch(() => [] as CrmContactCardScan[])])
      .then(([found, foundScans]) => {
        if (cancelled) return
        if ('moved_to' in found) {
          // Merged into another contact: go there, keeping the tab and the rest of the query.
          navigate(`/relaties/contacten/${found.moved_to}${location.search}`, { replace: true, state: location.state })
          return
        }
        loaded.current = true
        setDetail(found)
        setScans(foundScans)
        setLoadError(null)
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        if (caught instanceof CrmApiError && caught.status === 404) {
          setLoadError('notfound')
        } else if (loaded.current) {
          // A refresh that fails leaves the page as it was: it is not a reason to lose it.
          toast.error(caught instanceof Error ? caught.message : 'Verversen mislukt.')
        } else {
          setLoadError('failed')
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `location` is only read at the moment of a redirect
  }, [personId, factsKey])

  // The person an organisation was migrated from: the organisation is the page now.
  useEffect(() => {
    if (movedTo) navigate(`/relaties/organisaties/${movedTo}`, { replace: true })
  }, [movedTo, navigate])

  useEffect(() => {
    // The delete dialog was opened from the Meer menu, and the menu item that had the focus
    // is gone by then: when the dialog closes the focus goes back to the Meer button.
    if (confirmDelete) {
      askedDelete.current = true
    } else if (askedDelete.current) {
      askedDelete.current = false
      actionsRef.current?.querySelector<HTMLElement>('button[aria-haspopup]')?.focus()
    }
  }, [confirmDelete])

  /**
   * The answer to a change of the fields. The labels have their own path (`changeLabels`);
   * and an answer that is older than what is on the screen is dropped.
   */
  function showFields(updated: CrmContactDetail) {
    setDetail((current) => {
      if (!current) return updated
      if (isOlder(updated, current)) return current
      return { ...updated, labels: current.labels }
    })
  }

  async function patch(fields: CrmContactUpdate) {
    // Errors propagate: InlineProperty and SummaryEditor show them.
    showFields(await patchContact(personId, fields))
  }

  async function patchWithToast(fields: CrmContactUpdate, success?: string) {
    try {
      await patch(fields)
      if (success) toast.success(success)
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    }
  }

  const { changeOwner, dialog: ownerDialog } = useOwnerChange(relation, Boolean(detail?.owner), (fields) =>
    patchWithToast(fields),
  )

  /**
   * Label changes are shown at once and sent one after the other, each with the whole set as
   * it stood after that click. Built on the last answer instead, a quick second click would
   * send a set without the first one and take it away again.
   */
  function changeLabels(next: LabelLike[]) {
    setChosenLabels(next)
    labelSaves.current += 1
    labelQueue.current = labelQueue.current.then(async () => {
      try {
        const saved = await putContactLabels(
          personId,
          next.map((label) => label.id),
        )
        setDetail((current) => current && { ...current, labels: saved.labels })
      } catch (caught) {
        toast.error(caught instanceof Error ? caught.message : 'Labels opslaan mislukt.')
      } finally {
        labelSaves.current -= 1
        if (labelSaves.current === 0) setChosenLabels(null)
      }
    })
  }

  function selectTab(key: string) {
    // A request for the link form is only good for the visit it was made in.
    setLinkRequest(0)
    setParams(key === 'overzicht' ? {} : { tab: key }, { state: location.state })
  }

  function selectChip(key: ChipKey | null) {
    setParams(key ? { tab: 'activiteit', type: key } : { tab: 'activiteit' }, {
      replace: true,
      state: location.state,
    })
  }

  /** From a link inside the page: the tab opens, and the focus goes to it as it does with the arrow keys. */
  function showActivity() {
    selectTab('activiteit')
    focusTab(idPrefix, 'activiteit')
  }

  /**
   * A quick action: the composer opens on Overzicht (or stays on Activiteit) with the focus in
   * it. Every press is a request of its own, also while the composer is open already.
   */
  function openComposer(type: ComposerType) {
    if (tab !== 'overzicht' && tab !== 'activiteit') selectTab('overzicht')
    setComposer({ open: true, type, focus: true })
  }

  /** "Koppelen aan organisatie": on a phone the card lives on the Koppelingen tab, which appears first. */
  function requestLink() {
    if (!desktop && tab !== 'koppelingen') selectTab('koppelingen')
    setLinkRequest((count) => count + 1)
  }

  async function remove() {
    setDeleting(true)
    try {
      await deleteContact(personId)
      setConfirmDelete(false)
      navigate(listPath)
      toast.success('Contact verwijderd.')
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Verwijderen mislukt.')
    } finally {
      setDeleting(false)
    }
  }

  if (loadError) {
    return loadError === 'notfound' ? (
      <ErrorCard
        title="Contact niet gevonden"
        description="Dit contact bestaat niet (meer). Misschien is het verwijderd."
        onRetry={refreshFacts}
      />
    ) : (
      <ErrorCard title="Contact laden mislukt" onRetry={refreshFacts} />
    )
  }
  if (!detail || movedTo) {
    return (
      <div className="flex flex-col gap-4" aria-hidden>
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }
  const back = <RecordBackLink to={listPath}>Contacten</RecordBackLink>
  if (!detail.is_contact) return <NoRelation detail={detail} back={back} />

  const allAgreements = agreements ? [...agreements.own, ...agreements.via] : []
  const tabs: RecordTab[] = [
    ...TABS,
    {
      key: 'afspraken',
      label: 'Afspraken',
      ...(agreements ? { count: allAgreements.filter((agreement) => agreement.status === 'actief').length } : {}),
    },
  ]
  const editAgreement = (agreement: Agreement) => setAgreementDialog({ open: true, agreement })
  const primary = detail.organisations.find((membership) => membership.is_primary && !membership.ended_on)
  const archived = detail.status === 'inactief'
  // A summary from before E3 exists only as the plain `notes` of the old textarea. The editor
  // reads HTML, so those lines go in as the paragraph the backend would have written for them:
  // the line breaks stay and "Jan <jg@x.nl>" is not taken for a tag (it would vanish on the first save).
  const summaryHtml = detail.summary_html ?? (detail.notes?.trim() ? plainTextToHtml(detail.notes) : null)
  const composerRelation: ComposerRelation = {
    ...relation,
    name: detail.full_name,
    primaryOrganisation: primary ? { id: primary.organisation_id, name: primary.organisation_name } : null,
  }

  const actions: QuickAction[] = [
    { key: 'note', label: 'Notitie', icon: ACTIVITY_ICONS.note, onClick: () => openComposer('note') },
    {
      key: 'email',
      label: 'E-mail',
      icon: Mail,
      onClick: () => setMailOpen(true),
      disabled: !detail.email,
      hint: detail.email ? 'E-mail sturen' : 'Contact heeft geen e-mailadres',
    },
    { key: 'call', label: 'Gesprek', icon: ACTIVITY_ICONS.call, onClick: () => openComposer('call') },
    ...inspreken.quick,
    {
      key: 'meeting',
      label: 'Meeting',
      icon: ACTIVITY_ICONS.meeting,
      onClick: () => openComposer('meeting'),
    },
    { key: 'task', label: 'Taak', icon: ListChecks, onClick: () => setTaskOpen(true) },
  ]
  const more: QuickMenuItem[] = [
    ...inspreken.more,
    { key: 'agreement', label: 'Afspraak vastleggen', onSelect: () => setAgreementDialog({ open: true }) },
    { key: 'link', label: 'Koppelen aan organisatie', onSelect: requestLink },
    {
      key: 'archive',
      label: archived ? 'Heractiveren' : 'Archiveren',
      onSelect: () =>
        archived ? void patchWithToast({ status: 'actief' }, 'Contact weer actief.') : setArchiveOpen(true),
    },
    { key: 'merge', label: 'Samenvoegen met…', onSelect: () => setMergePicking(true) },
    { key: 'delete', label: 'Verwijderen', destructive: true, onSelect: () => setConfirmDelete(true) },
  ]

  // One composer for both panels (only one of them renders): its draft lives in sessionStorage.
  const composerCard = (
    <ActivityComposer
      relation={composerRelation}
      open={composer.open}
      type={composer.type}
      autoFocus={composer.focus}
      onAutoFocused={lowerFocusRequest}
      onOpenChange={(open) => setComposer((current) => ({ ...current, open, focus: false }))}
      onTypeChange={(type) => setComposer((current) => ({ ...current, type }))}
      onSaved={(item) => {
        if (item.type === 'call' || item.type === 'meeting') {
          clearCall()
          setCallEpoch((n) => n + 1)
        }
        refreshAll()
      }}
      onTaskCreated={refreshAll}
    />
  )
  const planned = <PlannedSection relation={relation} version={version} onChanged={refreshAll} />

  const overview = (
    <>
      <CallPromptBanner
        key={callEpoch}
        relation={{ type: 'contact', id: personId, name: detail.full_name }}
        onLogCall={() => openComposer('call')}
      />
      <MergeNotice kind="contact" relationId={personId} survivorName={detail.full_name} onUndone={refreshAll} />
      <RelationKeyFacts
        relationSince={detail.relation_since}
        lastContact={detail.last_contact}
        nextAction={detail.next_action}
        rhythm={agreements ? rhythmState(allAgreements) : null}
      />
      {composerCard}
      <SummaryEditor
        title={`Over ${detail.full_name}`}
        html={summaryHtml}
        placeholder={`Leg vast wat het team over ${detail.full_name} moet weten…`}
        draftKey={`crm:over-contact:${personId}`}
        edited={
          detail.summary_updated_at
            ? { at: detail.summary_updated_at, by: detail.summary_updated_by?.full_name ?? null }
            : null
        }
        onSave={(html) => patch({ summary_html: html })}
      />
      <AgreementsSection
        relation={relation}
        data={agreements}
        failed={agreementsFailed}
        onNew={() => setAgreementDialog({ open: true })}
        onEdit={editAgreement}
        onContact={() => openComposer('call')}
        onChanged={refreshAll}
      />
      {planned}
      <RecentActivity relation={relation} version={version} onShowAll={showActivity} />
    </>
  )
  const activity = (
    <>
      {composerCard}
      <Timeline
        relation={relation}
        typeKey={typeKey}
        onTypeKeyChange={selectChip}
        version={version}
        planned={planned}
        onChanged={refreshFacts}
      />
    </>
  )

  const agreementsTab = (
    <AgreementsTab
      relation={relation}
      name={detail.full_name}
      data={agreements}
      failed={agreementsFailed}
      onNew={() => setAgreementDialog({ open: true })}
      onEdit={editAgreement}
      onContact={() => openComposer('call')}
      onChanged={refreshAll}
    />
  )

  return (
    <>
      <RecordLayout
        back={back}
        label="Contact"
        header={
          <ContactHeader
            detail={detail}
            labels={chosenLabels ?? detail.labels}
            onLabelsChange={changeLabels}
            actions={actions}
            more={more}
            actionsRef={actionsRef}
          />
        }
        properties={
          <ContactProperties
            detail={detail}
            onPatch={patch}
            onPatchWithToast={patchWithToast}
            onDetail={showFields}
            onOwnerChange={changeOwner}
          />
        }
        tabs={tabs}
        activeTab={tab}
        onTabChange={selectTab}
        panels={{ overzicht: overview, activiteit: activity, afspraken: agreementsTab }}
        right={
          <ContactAssociations
            detail={detail}
            scans={scans}
            linkRequest={linkRequest}
            onChanged={refreshFacts}
          />
        }
        idPrefix={idPrefix}
      />
      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        relation={{ ...relation, name: detail.full_name }}
        onCreated={refreshAll}
        onMakeAgreement={() => setAgreementDialog({ open: true })}
      />
      {agreementDialog.open ? (
        <AgreementDialog
          open
          onOpenChange={(open) => {
            if (!open) setAgreementDialog({ open: false })
          }}
          party={{ kind: 'contact', id: personId, name: detail.full_name }}
          agreement={agreementDialog.agreement}
          defaultOwnerId={detail.owner?.id}
          onSaved={refreshAll}
        />
      ) : null}
      {ownerDialog}
      <ArchiveRelationDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        relation={relation}
        name={detail.full_name}
        onConfirm={(endAgreements) => {
          setArchiveOpen(false)
          void patchWithToast({ status: 'inactief', end_agreements: endAgreements }, 'Contact gearchiveerd.').then(refreshAll)
        }}
      />
      <ContactMailDialog
        open={mailOpen}
        onOpenChange={setMailOpen}
        contact={{
          person_id: detail.person_id,
          full_name: detail.full_name,
          email: detail.email,
          organisation: primary?.organisation_name ?? detail.organisation,
        }}
        onSent={refreshAll}
      />
      {mergePicking ? (
        <PickContactDialog
          excludeId={personId}
          onClose={() => setMergePicking(false)}
          onPicked={(pickedId) => {
            setMergePicking(false)
            setMergeWith(pickedId)
          }}
        />
      ) : null}
      {mergeWith ? (
        <MergeContactsDialog
          firstId={personId}
          secondId={mergeWith}
          survivorId={personId}
          onClose={() => setMergeWith(null)}
          onUndone={refreshAll}
          onMerged={(survivor) => {
            setMergeWith(null)
            if (survivor.id === personId) refreshAll()
            else navigate(`/relaties/contacten/${survivor.id}`)
          }}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Contact '${detail.full_name}' verwijderen?`}
        description="Het contact, zijn labels, lijsten en koppelingen verdwijnen. Dit kun je niet ongedaan maken."
        busy={deleting}
        onConfirm={() => void remove()}
      />
    </>
  )
}
